// src/components/CustomerFlow/Steps/OnSiteValidationStep.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import api from '../../../services/api';
import { Button } from '../../../components/ui';

const LOCATION_VALIDATION_BLOCKED_COPY = [
  'No pudimos confirmar que te encontrás en el comercio.',
  'Para continuar con el proceso es necesario encontrarse en el local al momento de realizar la validación.',
  'Por favor, contactá a tu vendedor para iniciar una nueva gestión.',
];

const OnSiteValidationStep = ({ 
  flowData, 
  stepStatuses,
  onNext
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const [hasAutoActivated, setHasAutoActivated] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const [isLocationBlocked, setIsLocationBlocked] = useState(false);

  // Auto-activar el paso si est? en pending (solo una vez)
  useEffect(() => {
    const currentStepStatus = stepStatuses['GeoLocationValidationStep'];
    
    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      setHasAutoActivated(true);
      
      onNext({
        step_name: 'GeoLocationValidationStep',
        step_status: 'in_progress',
        auto_activated: true,
        activation_timestamp: new Date().toISOString()
      }, 'in_progress');
    }
  }, [stepStatuses, onNext, hasAutoActivated]);

  // Helper function para convertir getCurrentPosition a Promise
  const getCurrentPositionPromise = (options) => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  };

  // Obtener ubicaci?n del usuario con estrategia en cascada
  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      throw new Error('Geolocalización no soportada');
    }

    // Intento rápido (menos preciso)
    try {
      // Intento preciso primero
      const position = await getCurrentPositionPromise({
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 0
      });

      return {
        lat: position.coords.latitude,
        lon: position.coords.longitude,
        accuracy: position.coords.accuracy,
        source: 'gps'
      };
    } catch (e1) {
      // Intento rápido si el preciso falla
      try {
        const position = await getCurrentPositionPromise({
          enableHighAccuracy: true,
          timeout: 4000,
          maximumAge: 60000
        });

        return {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracy: position.coords.accuracy,
          source: 'gps'
        };
      } catch (e2) {
        // Ambos intentos fallaron
        throw e2;
      }
    }
  };

  // Manejar errores de geolocalización
  const getGeolocationErrorMessage = (error) => {
    switch (error.code) {
      case 1: // PERMISSION_DENIED
        return 'Necesitamos acceso a tu ubicación. Por favor, permite el acceso en tu navegador.';
      case 2: // POSITION_UNAVAILABLE
        return 'No pudimos obtener tu ubicación. Por favor, verifica tu conexión.';
      case 3: // TIMEOUT
        return 'La solicitud de ubicación tardó demasiado. Por favor, intenta nuevamente.';
      default:
        return 'Error al obtener tu ubicación. Por favor, intenta nuevamente.';
    }
  };

  // Validar ubicación con el backend
  const validateLocation = async (coords) => {
    try {
      setIsValidating(true);
      setValidationError(null);

      if (!flowData?.tokenCustomerId || !flowData?.tokenPurchaseRequestId) {
        throw new Error('No se pudo obtener la información de la solicitud. Por favor, recarga la página.');
      }

      const response = await api.post(
        `/customers/${flowData.tokenCustomerId}/purchase-requests/${flowData.tokenPurchaseRequestId}/on-site-validation`,
        {
          latitude: coords.lat,
          longitude: coords.lon,
          source: coords.source || 'gps'
        }
      );

      if (response.data.success) {
        // éxito - marcar paso como completado
        onNext({ 
          step_name: 'GeoLocationValidationStep',
          step_status: 'completed',
          location_validated: true,
          is_on_site: response.data.is_on_site,
          coordinates: coords,
          validation_timestamp: new Date().toISOString()
        }, 'completed');
      } else {
        throw new Error(response.data.message || 'Error al validar la ubicación');
      }

    } catch (error) {
      console.error('Error validating location:', error);

      if (error.response?.data?.location_validation_blocked) {
        setIsLocationBlocked(true);
        setValidationError(null);
        setIsValidating(false);
        return;
      }
      
      const newAttemptCount = attemptCount + 1;
      setAttemptCount(newAttemptCount);

      let errorMessage = 'Error al validar la ubicación. Por favor, intenta nuevamente.';
      
      if (error.response?.status === 422) {
        // Errores de validación del backend
        const errors = error.response.data.errors;
        if (errors) {
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 500) {
        errorMessage = 'Error al procesar la validación. Por favor, intenta nuevamente.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setValidationError(errorMessage);
      setIsValidating(false);
    }
    // En éxito no deshabilitamos: mantenemos el botón deshabilitado hasta que el paso cambie
  };

  // Manejar click en botón "Validar Ubicación"
  const handleValidateLocation = async () => {
    try {
      setIsValidating(true);
      setValidationError(null);

      const coords = await getCurrentLocation();
      setCoordinates(coords);
      
      // Validar con backend (validateLocation maneja su propio estado de loading)
      await validateLocation(coords);

    } catch (error) {
      console.error('Error getting location:', error);
      
      const newAttemptCount = attemptCount + 1;
      setAttemptCount(newAttemptCount);

      let errorMessage;
      if (error.code !== undefined) {
        // Es un error de geolocalización
        errorMessage = getGeolocationErrorMessage(error);
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = 'Error al obtener tu ubicación. Por favor, intenta nuevamente.';
      }

      setValidationError(errorMessage);
      setIsValidating(false);
    }
  };

  return (
    <div className="on-site-validation-step">
      <div className="on-site-validation-step__header">
        <FontAwesomeIcon icon={faLocationDot} className="on-site-validation-step__icon" />
        <h3 className="on-site-validation-step__title">Confirma tu ubicación</h3>
      </div>

      <div className="step-body">
        {isLocationBlocked ? (
          <div className="on-site-validation-step__blocked" role="alert" aria-live="polite">
            {LOCATION_VALIDATION_BLOCKED_COPY.map((paragraph) => (
              <p key={paragraph} className="on-site-validation-step__blocked-text">
                {paragraph}
              </p>
            ))}
          </div>
        ) : (
          <div className="on-site-validation-step__info">
            <p className="on-site-validation-step__lead">
              Para continuar necesitamos verificar que te encontras en el comercio.
            </p>
            <div className="on-site-validation-step__hint" aria-label="Cómo habilitar la ubicación">
              <p className="on-site-validation-step__hint-title">Que tenés que hacer</p>
              <ul className="on-site-validation-step__hint-list">
                <li>Presiona el botón de abajo para iniciar la validación.</li>
                <li>Cuando el navegador lo solicite, elegí <strong>Permitir</strong>.</li>
                <li>Mantén activada la ubicación del teléfono hasta completar el paso.</li>
              </ul>
            </div>
          </div>
        )}

        {validationError && !isLocationBlocked && (
          <div className="alert alert-light-danger mb-4" role="alert" aria-live="polite">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {validationError}
          </div>
        )}

        {/* Botón principal: Confirma tu ubicación */}
        {!isLocationBlocked && (
          <Button
            variant="primary"
            size="lg"
            fullWidth
            className="on-site-validation-step__primary-btn"
            onClick={handleValidateLocation}
            disabled={isValidating}
          >
            {isValidating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Obteniendo ubicación...
              </>
            ) : attemptCount > 0 ? (
              'Reintentar'
            ) : (
              'Confirmar ubicación'
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default OnSiteValidationStep;
